import { useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Megaphone, Plus, Eye, MousePointerClick, Upload, Loader2, Pause, Play, Trash2, Image as ImageIcon } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { ListingPicker, type PickedListing } from './ListingPicker';
import { ETHIOPIAN_CITIES } from '@/lib/ethiopianCities';

const PAGES = ['homepage', 'buy', 'rent', 'projects', 'plots', 'commercial', 'agencies', 'agents'];
const AGENCY_TIERS = ['titanium', 'platinum', 'gold', 'silver', 'standard'];
const STATUS_VARIANT: Record<string, any> = {
  active: 'default', paused: 'secondary', draft: 'outline', expired: 'outline', archived: 'outline',
};

const detailUrlFor = (l: PickedListing) => {
  switch (l.type) {
    case 'property': return `/properties/${l.id}`;
    case 'commercial': return `/commercial/${l.id}`;
    case 'plot': return `/plots/${l.id}`;
    case 'project': return `/projects/${l.id}`;
  }
};

interface Placement {
  id: string; code: string; name: string;
  width_px: number | null; height_px: number | null;
  available_spots: number; pages: string[]; tier_label: string | null;
}

const blankForm = {
  advertiser_name: '', advertiser_contact: '',
  title: '', description: '',
  image_url: '', target_url: '',
  placement_id: '' as string,
  pages: [] as string[],
  target_agency_tiers: [] as string[],
  target_cities: [] as string[],
  tier: 'standard' as 'standard' | 'premium',
  start_date: format(new Date(), 'yyyy-MM-dd'),
  end_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
  price_paid: '',
  payment_ref: '',
  status: 'active' as 'draft' | 'active' | 'paused',
};

export function AdCampaignsManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [linkedListing, setLinkedListing] = useState<PickedListing | null>(null);
  const [filterPlacement, setFilterPlacement] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(blankForm);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: placements } = useQuery({
    queryKey: ['banner-placements-catalog'],
    queryFn: async () => {
      const { data } = await supabase.from('banner_placements' as any).select('*').eq('is_active', true).order('sort_order');
      return (data as unknown as Placement[]) || [];
    },
  });

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['ad-campaigns'],
    queryFn: async () => {
      const { data } = await supabase
        .from('ad_campaigns')
        .select('*, banner_placements(name, code, width_px, height_px)')
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  const selectedPlacement = useMemo(
    () => placements?.find((p) => p.id === form.placement_id),
    [placements, form.placement_id]
  );

  const togglePage = (p: string) =>
    setForm((f) => ({ ...f, pages: f.pages.includes(p) ? f.pages.filter((x) => x !== p) : [...f.pages, p] }));
  const toggleTier = (t: string) =>
    setForm((f) => ({ ...f, target_agency_tiers: f.target_agency_tiers.includes(t) ? f.target_agency_tiers.filter((x) => x !== t) : [...f.target_agency_tiers, t] }));
  const toggleCity = (c: string) =>
    setForm((f) => ({ ...f, target_cities: f.target_cities.includes(c) ? f.target_cities.filter((x) => x !== c) : [...f.target_cities, c] }));

  const onPickPlacement = (id: string) => {
    const p = placements?.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      placement_id: id,
      pages: p?.pages?.length ? p.pages : f.pages,
      tier: (p?.tier_label?.toLowerCase().includes('premium') ? 'premium' : 'standard') as any,
    }));
  };

  const handleUpload = async (file: File) => {
    if (!user) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from('ad-banners').upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw error;
      const { data: pub } = supabase.storage.from('ad-banners').getPublicUrl(path);
      setForm((f) => ({ ...f, image_url: pub.publicUrl }));
      toast.success('Image uploaded');
    } catch (e: any) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setLinkedListing(null);
    setOpen(true);
  };

  const openEdit = (c: any) => {
    setEditingId(c.id);
    setForm({
      advertiser_name: c.advertiser_name || '',
      advertiser_contact: c.advertiser_contact || '',
      title: c.title || '',
      description: c.description || '',
      image_url: c.image_url || '',
      target_url: c.target_url || '',
      placement_id: c.placement_id || '',
      pages: Array.isArray(c.pages) ? c.pages : [],
      target_agency_tiers: Array.isArray(c.target_agency_tiers) ? c.target_agency_tiers : [],
      target_cities: Array.isArray(c.target_cities) ? c.target_cities : [],
      tier: (c.tier as any) || 'standard',
      start_date: c.start_date ? format(new Date(c.start_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      end_date: c.end_date ? format(new Date(c.end_date), 'yyyy-MM-dd') : format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      price_paid: c.price_paid?.toString() || '',
      payment_ref: c.payment_ref || '',
      status: c.status || 'active',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!user) return;
    if (!form.advertiser_name || !form.title || !form.image_url) {
      toast.error('Advertiser, title and image are required');
      return;
    }
    if (!form.placement_id) {
      toast.error('Pick a placement');
      return;
    }
    const placementCode = placements?.find((p) => p.id === form.placement_id)?.code || '';
    const payload: any = {
      advertiser_name: form.advertiser_name,
      advertiser_contact: form.advertiser_contact || null,
      title: form.title,
      description: form.description || null,
      image_url: form.image_url,
      target_url: form.target_url || null,
      placement_id: form.placement_id,
      placement: placementCode, // legacy text col, kept in sync
      pages: form.pages,
      target_agency_tiers: form.target_agency_tiers,
      target_cities: form.target_cities,
      tier: form.tier,
      start_date: form.start_date,
      end_date: form.end_date,
      price_paid: parseFloat(form.price_paid) || 0,
      payment_ref: form.payment_ref || null,
      status: form.status,
      is_active: form.status === 'active',
    };
    const op = editingId
      ? supabase.from('ad_campaigns').update(payload).eq('id', editingId)
      : supabase.from('ad_campaigns').insert({ ...payload, created_by: user.id });
    const { error } = await op;
    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? 'Campaign updated' : 'Campaign created');
    qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
    setOpen(false);
  };

  const setStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from('ad_campaigns')
      .update({ status, is_active: status === 'active' } as any)
      .eq('id', id);
    if (error) return toast.error(error.message);
    toast.success(`Campaign ${status}`);
    qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this campaign?')) return;
    const { error } = await supabase.from('ad_campaigns').delete().eq('id', id);
    if (error) return toast.error(error.message);
    toast.success('Deleted');
    qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
  };

  const filtered = (campaigns || []).filter((c: any) => {
    if (filterPlacement !== 'all' && c.placement_id !== filterPlacement) return false;
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (filterCity !== 'all') {
      const cities: string[] = Array.isArray(c.target_cities) ? c.target_cities : [];
      if (filterCity === 'none' && cities.length !== 0) return false;
      if (filterCity !== 'none' && !cities.includes(filterCity)) return false;
    }
    return true;
  });

  const placementSupportsCities = !!selectedPlacement &&
    (selectedPlacement.code.includes('search') || selectedPlacement.code.includes('category'));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" /> Ad Campaigns</CardTitle>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> New Banner</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium">Filter:</span>
          <Select value={filterPlacement} onValueChange={setFilterPlacement}>
            <SelectTrigger className="h-8 w-auto min-w-[180px] text-xs"><SelectValue placeholder="Placement" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All placements</SelectItem>
              {placements?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCity} onValueChange={setFilterCity}>
            <SelectTrigger className="h-8 w-auto min-w-[150px] text-xs"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any city target</SelectItem>
              <SelectItem value="none">No city filter</SelectItem>
              {ETHIOPIAN_CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? <Skeleton className="h-32" /> : !filtered.length ? (
          <p className="text-sm text-muted-foreground text-center py-8">No campaigns match these filters</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banner</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead>Cities</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right"><Eye className="h-3 w-3 inline" /></TableHead>
                  <TableHead className="text-right"><MousePointerClick className="h-3 w-3 inline" /></TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c: any) => {
                  const start = c.start_date ? new Date(c.start_date) : null;
                  const end = new Date(c.end_date);
                  const daysLeft = differenceInDays(end, new Date());
                  const cities: string[] = Array.isArray(c.target_cities) ? c.target_cities : [];
                  const placementName = c.banner_placements?.name || c.placement?.replace(/_/g, ' ');
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {c.image_url
                            ? <img src={c.image_url} alt={c.title} className="h-8 w-12 object-cover rounded border" />
                            : <div className="h-8 w-12 rounded border bg-muted flex items-center justify-center"><ImageIcon className="h-3 w-3 text-muted-foreground" /></div>}
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate max-w-[180px]">{c.title}</div>
                            <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{c.advertiser_name}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="capitalize">{placementName || '—'}</div>
                        {c.banner_placements?.width_px && (
                          <div className="text-[10px] text-muted-foreground">
                            {c.banner_placements.width_px}×{c.banner_placements.height_px}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {cities.length === 0 ? <span className="text-muted-foreground">All</span> : (
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {cities.slice(0, 2).map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
                            {cities.length > 2 && <Badge variant="outline" className="text-[10px]">+{cities.length - 2}</Badge>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[c.status] || 'secondary'} className="capitalize">{c.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs">{(c.impressions || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-right text-xs">{(c.clicks || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">
                        <div>{start ? format(start, 'MMM d') : '—'} → {format(end, 'MMM d')}</div>
                        <div className={`text-[10px] ${daysLeft < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {daysLeft < 0 ? `Expired ${-daysLeft}d ago` : `${daysLeft}d left`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(c)} title="Edit"><Megaphone className="h-3.5 w-3.5" /></Button>
                          {c.status === 'active' ? (
                            <Button size="icon" variant="ghost" onClick={() => setStatus(c.id, 'paused')} title="Pause"><Pause className="h-3.5 w-3.5" /></Button>
                          ) : (
                            <Button size="icon" variant="ghost" onClick={() => setStatus(c.id, 'active')} title="Resume"><Play className="h-3.5 w-3.5" /></Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => remove(c.id)} title="Delete"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Banner' : 'New Banner'}</DialogTitle>
            <DialogDescription>Upload a banner image and assign it to a placement, schedule and targeting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Placement first */}
            <div>
              <Label>Placement *</Label>
              <Select value={form.placement_id} onValueChange={onPickPlacement}>
                <SelectTrigger><SelectValue placeholder="Pick a defined placement…" /></SelectTrigger>
                <SelectContent>
                  {placements?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.width_px ? `· ${p.width_px}×${p.height_px}` : ''} · {p.available_spots} spots
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPlacement && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Recommended size: <strong>{selectedPlacement.width_px}×{selectedPlacement.height_px}px</strong> · {selectedPlacement.available_spots} spots available
                </p>
              )}
            </div>

            {/* Linked listing optional */}
            <div>
              <Label className="mb-2 block">Linked Listing (optional)</Label>
              <ListingPicker
                value={linkedListing}
                onChange={(l) => {
                  setLinkedListing(l);
                  if (l) setForm((f) => ({
                    ...f,
                    target_url: f.target_url || `${window.location.origin}${detailUrlFor(l)}`,
                    title: f.title || l.title,
                    image_url: f.image_url || l.image_url || '',
                  }));
                }}
              />
            </div>

            {/* Image upload */}
            <div>
              <Label>Banner Image *</Label>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://… or upload" />
                  <div className="flex items-center gap-2 mt-1.5">
                    <input ref={fileRef} type="file" accept="image/*" hidden
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
                    <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                      {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                      {uploading ? 'Uploading…' : 'Upload image'}
                    </Button>
                    {selectedPlacement?.width_px && (
                      <span className="text-[11px] text-muted-foreground">Best at {selectedPlacement.width_px}×{selectedPlacement.height_px}</span>
                    )}
                  </div>
                </div>
                {form.image_url && (
                  <img src={form.image_url} alt="preview" className="h-16 w-24 object-cover border rounded" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Advertiser Name *</Label><Input value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })} /></div>
              <div><Label>Advertiser Contact</Label><Input value={form.advertiser_contact} onChange={(e) => setForm({ ...form, advertiser_contact: e.target.value })} /></div>
            </div>
            <div><Label>Banner Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Click-through URL</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://…" /></div>

            {/* Schedule */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Start date *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End date *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v: any) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pages */}
            <div>
              <Label className="mb-2 block">Show on Pages</Label>
              <div className="flex flex-wrap gap-3">
                {PAGES.map((p) => (
                  <div key={p} className="flex items-center gap-2">
                    <Checkbox id={`pg-${p}`} checked={form.pages.includes(p)} onCheckedChange={() => togglePage(p)} />
                    <Label htmlFor={`pg-${p}`} className="font-normal capitalize cursor-pointer">{p}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* City targeting */}
            <div>
              <Label className="mb-1 block">
                Target Cities {placementSupportsCities && <Badge variant="default" className="ml-1 text-[10px]">Recommended for this placement</Badge>}
              </Label>
              <p className="text-[11px] text-muted-foreground mb-2">
                Only show this banner to visitors browsing the selected cities. Leave empty to show in all cities.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ETHIOPIAN_CITIES.map((c) => {
                  const active = form.target_cities.includes(c);
                  return (
                    <Badge key={c} variant={active ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleCity(c)}>
                      {c}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Agency tiers */}
            <div>
              <Label className="mb-2 block">Target Agency Tiers (optional)</Label>
              <div className="flex flex-wrap gap-3">
                {AGENCY_TIERS.map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <Checkbox id={`tier-${t}`} checked={form.target_agency_tiers.includes(t)} onCheckedChange={() => toggleTier(t)} />
                    <Label htmlFor={`tier-${t}`} className="font-normal capitalize cursor-pointer">{t}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price (ETB)</Label><Input type="number" value={form.price_paid} onChange={(e) => setForm({ ...form, price_paid: e.target.value })} /></div>
              <div><Label>Payment Ref</Label><Input value={form.payment_ref} onChange={(e) => setForm({ ...form, payment_ref: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Create banner'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
